<script>
  import { createEventDispatcher } from 'svelte';
  import CodeMirror from 'svelte-codemirror-editor';
  import { autocompletion } from '@codemirror/autocomplete';
  import { indentLess, indentMore } from '@codemirror/commands';
  import { html } from '@codemirror/lang-html';
  import { bracketMatching, /*foldGutter,*/ indentOnInput } from '@codemirror/language';
  import { linter, lintGutter } from '@codemirror/lint';
  import { search } from '@codemirror/search';
  import { oneDark } from '@codemirror/theme-one-dark';
  import { EditorView, keymap } from '@codemirror/view';
  import { indentationMarkers } from '@replit/codemirror-indentation-markers';

  import { formatHTML } from '@client/utils/html-formatter';

  import { opensenseTemplateAutocomplete, variableAutocomplete } from './autocomplete';
  import {
    bannerHighlighting,
    bannerStyles,
    liquidHighlighting,
    liquidStyles,
    opensenseHighlighting,
    opensenseStyles,
    pluginHighlighting,
    pluginStyles,
    variableHighlighting,
    variableStyles
  } from './decorators';
  import { htmlLinter, liquidLinter } from './linters';
  import { liquidAutoCloseTags, liquidLanguage } from './parsers';
  import { styles } from './styles';

  /**
   * Represents the content of the code.
   * @type {string}
   * @default ''
   */
  export let value = '';

  /**
   * Determines if longer lines are wrapped
   * @type {boolean}
   * @default false
   */
  export let lineWrapping = false;

  /**
   * Determines if the tab key is used for indentation or navigation.
   * @type {boolean}
   * @default true
   */
  export let useTab = true;

  /**
   * Contains additional data or parameters for the CodeMirror.
   * @type {Object}
   * @default {}
   */
  export let data = {};

  /**
   * Determines if the editor should auto-format on blur.
   * @type {Object}
   * @default false
   */
  export let disableFormatting = false;

  /**
   * Formats the HTML content on load.
   * @type {boolean}
   * @default false
   */
  export let formatOnLoad = false;

  const dispatch = createEventDispatcher();

  const editorEventHandlers = EditorView.domEventHandlers({
    blur: async () => {
      if (!disableFormatting) {
        value = await formatHTML(value);
      }

      return true;
    },
    keyup: (event, view) => {
      const htmlLintErrors = htmlLinter(view.viewState);
      const liquidLintErrors = liquidLinter(view.viewState);

      dispatch('htmlLintErrors', htmlLintErrors);
      dispatch('liquidLintErrors', liquidLintErrors);
    }
  });

  const extensions = [
    indentationMarkers(),
    // foldGutter(),
    bracketMatching(),
    indentOnInput(),
    keymap.of([indentMore, indentLess]),
    lintGutter(),
    autocompletion(),
    linter(htmlLinter),
    linter(liquidLinter),
    bannerHighlighting,
    bannerStyles,
    liquidHighlighting,
    liquidStyles,
    opensenseHighlighting,
    opensenseStyles,
    opensenseTemplateAutocomplete(data?.params),
    pluginHighlighting,
    pluginStyles,
    variableHighlighting,
    variableStyles,
    variableAutocomplete(data?.params),
    editorEventHandlers,
    liquidAutoCloseTags,
    liquidLanguage,
    html(),
    search({ top: true })
  ];

  if (formatOnLoad) {
    if (!disableFormatting) {
      setTimeout(async () => {
        value = await formatHTML(value);
      }, 0);
    }
  }
</script>

<CodeMirror bind:value bind:useTab bind:lineWrapping theme={oneDark} {extensions} {styles} {...$$restProps} />
